import { initializeApp } from 'firebase/app';
import { initializeAuth, GoogleAuthProvider, OAuthProvider, TwitterAuthProvider, browserLocalPersistence, indexedDBLocalPersistence, setPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { initializeAnalytics, isSupported, setUserProperties } from 'firebase/analytics';
import { currentAnalyticsTraffic } from './services/analyticsEnvironment';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check
if (typeof window !== 'undefined') {
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.startsWith('192.168.');
  
  // For development, use debug token from environment variable
  if ((process.env.NODE_ENV === 'development' || isLocalhost) && process.env.REACT_APP_DEBUG_TOKEN) {
    // Use debug token from environment variable
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_DEBUG_TOKEN;
    console.log('App Check debug mode enabled with configured token');
  }
  
  // Only initialize App Check if explicitly enabled (disabled by default due to reCAPTCHA domain issues)
  const shouldInitAppCheck = process.env.REACT_APP_ENABLE_APP_CHECK === 'true';
  
  if (shouldInitAppCheck && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true
      });
      console.log('App Check initialized successfully');
    } catch (error) {
      console.error('Failed to initialize App Check:', error);
      console.log('Continuing without App Check');
    }
  } else if (!shouldInitAppCheck) {
    console.log('App Check disabled for development');
  } else {
    console.warn('REACT_APP_RECAPTCHA_SITE_KEY not set - App Check not initialized');
    console.log('To fix Storage/Firestore access:');
    console.log('1. Go to Firebase Console → App Check');
    console.log('2. Register your app and get reCAPTCHA site key');
    console.log('3. Add REACT_APP_RECAPTCHA_SITE_KEY to .env file');
  }
}

// Initialize Firebase services
// `initializeAuth`, not `getAuth` (BUGS #401, fix 3). `getAuth` also installs
// the popup/redirect resolver, which loads a hidden ~92KB auth iframe and a
// project-config request on EVERY page, anonymous blog readers included. Here
// the resolver is left out and passed explicitly at each popup/redirect call
// (Firebase's documented way to keep it off the boot path). Persistence lists
// localStorage first (what `setPersistence` below settles on) and IndexedDB
// second, so a session saved under either is still found and migrated.
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence],
});
console.log('🔥 Firebase Auth initialized:', {
  currentUser: auth.currentUser?.email || 'none',
  config: auth.config.apiKey.substring(0, 10) + '...'
});

export const googleProvider = new GoogleAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');
export const twitterProvider = new TwitterAuthProvider();
// `initializeFirestore` (not `getFirestore`) is the only API that lets
// us set `ignoreUndefinedProperties` — and we MUST set it true.
//
// Why: the Web SDK rejects any document containing a field with
// `undefined` as the value, throwing `FirebaseError: Function setDoc()
// called with invalid data. Unsupported field value: undefined …` and
// aborting the entire write. The Admin SDK ignores undefined fields by
// default; only the Web SDK is strict, and the difference catches
// every team that hasn't been bitten yet.
//
// Pre-2026-06-14 we shipped this bug class repeatedly: portfolio saves
// failed silently because `costBasis: undefined` was in the holding
// object literal, user-profile updates dropped on `avatarUrl: undefined`
// for OAuth users without a photo, levelUp-progress writes died on
// first quiz because `lastLevelUpAt: undefined` was being spread, and
// risk-report saves intermittently failed because backend responses
// had varying optional fields. ONE setting kills all of them at the
// SDK boundary. We still prefer not writing undefined deliberately —
// `initializeFirestore` is the safety net, not the design.
//
// `initializeFirestore` must be called BEFORE any other Firestore API
// touches the app; `getFirestore` after this point returns the same
// instance.
initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  // Auto-detect a blocked streaming transport and fall back to long-polling.
  // The default WebChannel transport streams `Listen` over QUIC/HTTP3, which some
  // networks reset — corporate proxies, VPNs, a few ad-blockers, and QUIC
  // middleboxes surface `ERR_QUIC_PROTOCOL_ERROR.QUIC_PUBLIC_RESET` +
  // `WebChannelConnection RPC 'Listen' stream transport errored` (+ a 400 on the
  // channel), which stalls realtime `onSnapshot` listeners (the notification bell,
  // live risk/report updates). This probes on startup and switches to long-polling
  // only when streaming is actually blocked, so healthy networks keep streaming.
  experimentalAutoDetectLongPolling: true,
});
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Connect to Firebase emulators in development when USE_AUTH_EMULATOR is set
const useEmulators = process.env.REACT_APP_USE_AUTH_EMULATOR === 'true';
if (useEmulators && typeof window !== 'undefined') {
  console.log('🔌 Connecting to Firebase emulators...');
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('✅ Connected to Firebase emulators (Auth:9099, Firestore:8080, Functions:5001, Storage:9199)');
  } catch (error) {
    console.error('❌ Failed to connect to emulators:', error);
  }
}

// Set auth persistence to LOCAL (persists even when browser closed)
// This stores auth tokens securely in IndexedDB/localStorage
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('✅ Auth persistence set to LOCAL');
    console.log('📱 Current user after persistence:', auth.currentUser?.email || 'none');
  })
  .catch((error) => {
    console.error('❌ Error setting auth persistence:', error);
  });

// Configure Google Provider
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Configure Microsoft Provider
microsoftProvider.setCustomParameters({
  prompt: 'select_account',
  // Don't specify PKCE parameters - let Firebase handle the OAuth flow
  // Firebase will use the implicit flow which works better for SPAs
  tenant: 'common' // Allow both personal and work accounts
});

// Add required scopes for Microsoft
microsoftProvider.addScope('openid');
microsoftProvider.addScope('profile');
microsoftProvider.addScope('email');

// Initialize Analytics (only in browser environment and if enabled).
// Every environment reports into the one property, so every event is labelled
// with where it came from — staging, previews, local runs and automated test
// browsers carry `traffic_type: internal` and never mix with production
// visitors ([[BUGS #527]], `analyticsEnvironment`).
let analytics: any = null;
const analyticsEnabled = process.env.REACT_APP_ENABLE_ANALYTICS !== 'false';

// Resolves once the SDK has decided (instance, or null when unsupported or
// disabled). Consumers that buffered events while `analytics` was still null
// flush on this — see analyticsService ([[BUGS #140]]: checking readiness
// synchronously at import time meant readiness could never be observed).
export const analyticsReady: Promise<any> =
  typeof window !== 'undefined' && analyticsEnabled
    ? isSupported()
        .then(supported => {
          if (supported) {
            // In the tag's own config call, so EVERY hit carries the labels —
            // the automatic first page view included. `setDefaultEventParameters`
            // was tried first: Firebase applies it after that page view, and
            // custom keys set that way never reached the hits (seen in the
            // browser 2026-10-25). `traffic_type` travels as `tt`.
            const traffic = currentAnalyticsTraffic();
            analytics = initializeAnalytics(app, { config: { ...traffic.params } });
            setUserProperties(analytics, { app_env: traffic.appEnv, automated: traffic.params.automated });
            console.log(`Firebase Analytics initialized (${traffic.appEnv}${traffic.automated ? ', automated' : ''})`);
          }
          return analytics;
        })
        .catch((e) => {
          // Off, and said so: a failed start drops events, it never mixes them.
          console.warn('Firebase Analytics did not start:', e);
          return null;
        })
    : Promise.resolve(null);

if (!analyticsEnabled) {
  console.log('Firebase Analytics disabled via REACT_APP_ENABLE_ANALYTICS=false');
}

export { analytics };
export default app;